import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MoodFace, getMoodBackgroundGradient, getMoodLabel } from './MoodFace';

interface MoodWidgetTranslations {
  labels: Record<string, string>;
}

interface MoodWidgetProps {
  /** Current mood value (0-100) or null */
  mood: number | null;
  /** Callback when mood changes, returns promise with celebration flag */
  onMoodChange: (mood: number) => Promise<{ showCelebration: boolean }>;
  /** Translations for mood labels */
  translations: MoodWidgetTranslations;
  /** Whether a save operation is in progress */
  isLoading?: boolean;
  /** Optional class name */
  className?: string;
}

const ANCHORS = [0, 25, 50, 75, 100] as const;
const NUDGE_OFFSETS = [-12, -6, 0, 6, 12] as const;

function nearestAnchor(mood: number): number {
  return ANCHORS.reduce((best, a) =>
    Math.abs(a - mood) < Math.abs(best - mood) ? a : best
  );
}

function offsetsForAnchor(anchor: number): readonly number[] {
  return NUDGE_OFFSETS.filter((o) => {
    const v = anchor + o;
    return v >= 0 && v <= 100;
  });
}

function nearestOffset(anchor: number, mood: number): number {
  const valid = offsetsForAnchor(anchor);
  return valid.reduce((best, o) =>
    Math.abs(anchor + o - mood) < Math.abs(anchor + best - mood) ? o : best
  );
}

/**
 * MoodWidget — anchor + nudge mood selector.
 *
 * Tier 1: tap one of 5 smileys to pick the mood family.
 * Tier 2: nudge row appears under the selected anchor with 5 dots
 *         (-12, -6, 0, +6, +12) for fine-tuning. Endpoints (0, 100)
 *         only get inward-direction dots.
 */
export function MoodWidget({
  mood,
  onMoodChange,
  translations,
  isLoading = false,
  className = '',
}: MoodWidgetProps) {
  const initialAnchor = mood === null ? null : nearestAnchor(mood);
  const initialOffset = mood === null || initialAnchor === null ? 0 : nearestOffset(initialAnchor, mood);

  const [selectedAnchor, setSelectedAnchor] = useState<number | null>(initialAnchor);
  const [selectedOffset, setSelectedOffset] = useState<number>(initialOffset);

  // Sync local state when the mood prop changes from outside
  useEffect(() => {
    if (mood === null) {
      setSelectedAnchor(null);
      setSelectedOffset(0);
    } else {
      const a = nearestAnchor(mood);
      setSelectedAnchor(a);
      setSelectedOffset(nearestOffset(a, mood));
    }
  }, [mood]);

  const committedMood = useMemo(() => {
    if (selectedAnchor === null) return null;
    return selectedAnchor + selectedOffset;
  }, [selectedAnchor, selectedOffset]);

  const handleAnchorClick = useCallback(
    async (anchor: number) => {
      if (isLoading) return;
      setSelectedAnchor(anchor);
      setSelectedOffset(0);
      await onMoodChange(anchor);
    },
    [isLoading, onMoodChange]
  );

  const handleNudgeClick = useCallback(
    async (anchor: number, offset: number) => {
      if (isLoading) return;
      setSelectedAnchor(anchor);
      setSelectedOffset(offset);
      await onMoodChange(anchor + offset);
    },
    [isLoading, onMoodChange]
  );

  const moodLabel = getMoodLabel(committedMood, translations.labels);
  const backgroundGradient = getMoodBackgroundGradient(committedMood);

  return (
    <div className={`card shadow-traq-md border-0 rounded-traq ${className}`}>
      <div className="card-body p-0">
        {/* Display area: label + big animated face on mood-colored gradient */}
        <div
          className="mood-scale_display-container"
          style={{ background: backgroundGradient }}
        >
          <div className="mood-scale_content-wrapper">
            <div className="mood-scale_side-info mood-scale_side-info--top">
              <div className="mood-scale_mood-label">{moodLabel}</div>
            </div>
            <div className="mood-scale_face-wrapper">
              <div className="text-center mood-scale_face-container">
                <MoodFace mood={committedMood} />
              </div>
            </div>
          </div>
        </div>

        {/* Selector: anchor row + contextual nudge row */}
        <div className="mood-picker_selector" role="radiogroup" aria-label="Mood selector">
          <div className="mood-picker_anchor-row">
            {ANCHORS.map((anchor) => {
              const isSelected = selectedAnchor === anchor;
              return (
                <div className="mood-picker_anchor-cell" key={anchor}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={getMoodLabel(anchor, translations.labels)}
                    disabled={isLoading}
                    onClick={() => handleAnchorClick(anchor)}
                    className={`mood-picker_anchor-btn${isSelected ? ' is-selected' : ''}`}
                  >
                    <MoodFace mood={anchor} />
                  </button>
                  {isSelected && (
                    <NudgeRow
                      anchor={anchor}
                      selectedOffset={selectedOffset}
                      disabled={isLoading}
                      onSelect={(offset) => handleNudgeClick(anchor, offset)}
                      labels={translations.labels}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

interface NudgeRowProps {
  anchor: number;
  selectedOffset: number;
  disabled: boolean;
  onSelect: (offset: number) => void;
  labels: Record<string, string>;
}

function NudgeRow({ anchor, selectedOffset, disabled, onSelect, labels }: NudgeRowProps) {
  const offsets = offsetsForAnchor(anchor);
  return (
    <div className="mood-picker_nudge-row">
      {offsets.map((offset) => {
        const value = anchor + offset;
        const isCenter = offset === 0;
        const isSelected = offset === selectedOffset;
        const label = getMoodLabel(value, labels);
        return (
          <button
            key={offset}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={label}
            title={label}
            disabled={disabled}
            onClick={() => onSelect(offset)}
            className={[
              'mood-picker_nudge-dot',
              isCenter ? 'is-center' : '',
              isSelected ? 'is-selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="mood-picker_nudge-dot-visual" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
